import { useMemo, useState } from 'react'
import ApiService from '@services/api'
import ChartComponent from '@components/dashboard/ChartComponent'
import { useNotification } from '@context/NotificationContext'
import { VISUAL_COLORS } from '@config/visualTheme'

const EMPTY_FILTERS = { dataInicio: '', dataFim: '' }

function formatNumber(value) {
  return new Intl.NumberFormat('pt-BR').format(value || 0)
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}

function formatDateTime(value) {
  return value ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) : '—'
}

function Stat({ icon, label, value, detail, tone = '' }) {
  return <article className={`report-stat ${tone}`}><div><span>{label}</span><strong>{value}</strong>{detail ? <small>{detail}</small> : null}</div><i className={`fas ${icon}`} /></article>
}

function Ranking({ title, items }) {
  return (
    <section className="report-card report-ranking">
      <div className="report-card-title"><h3>{title}</h3><span>Top {Math.min(items?.length || 0, 12)}</span></div>
      {items?.length ? items.map((item) => <div className="report-ranking-row" key={item.nome}><div><span>{item.nome}</span><small>{item.percentual}% do total</small></div><strong>{formatNumber(item.total)}</strong></div>) : <div className="report-empty compact">Sem dados para o período selecionado.</div>}
    </section>
  )
}

export default function ClickBusReport() {
  const notify = useNotification()
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(null)
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const valueChart = useMemo(() => ({
    labels: (dashboard?.evolucaoMensal || []).map((item) => {
      const [year, month] = item.competencia.split('-')
      return `${month}/${year}`
    }),
    datasets: [
      { label: 'Valor da causa', data: (dashboard?.evolucaoMensal || []).map((item) => item.valorCausa), backgroundColor: VISUAL_COLORS.emerald },
      { label: 'Provisão ClickBus', data: (dashboard?.evolucaoMensal || []).map((item) => item.valorProvisao), backgroundColor: VISUAL_COLORS.violet },
      { label: 'Indenização ClickBus', data: (dashboard?.evolucaoMensal || []).map((item) => item.indenizacaoClickBus), backgroundColor: VISUAL_COLORS.teal },
      { label: 'Indenização viação', data: (dashboard?.evolucaoMensal || []).map((item) => item.indenizacaoViacao), backgroundColor: VISUAL_COLORS.amber },
    ],
  }), [dashboard])

  const countChart = useMemo(() => ({
    labels: (dashboard?.evolucaoMensal || []).map((item) => {
      const [year, month] = item.competencia.split('-')
      return `${month}/${year}`
    }),
    datasets: [{ label: 'Processos', data: (dashboard?.evolucaoMensal || []).map((item) => item.totalProcessos), backgroundColor: VISUAL_COLORS.teal }],
  }), [dashboard])

  const loadDashboard = async (selectedFilters) => {
    setLoading(true)
    setError('')
    const result = await ApiService.getClickBusReportDashboard(selectedFilters)
    if (result.success) {
      setDashboard(result.data)
      setAppliedFilters({ ...selectedFilters })
    } else setError(result.error || 'Não foi possível carregar o relatório ClickBus.')
    setLoading(false)
  }

  const applyFilters = async (event) => {
    event.preventDefault()
    if (filters.dataInicio && filters.dataFim && filters.dataInicio > filters.dataFim) {
      setError('A data inicial não pode ser posterior à data final.')
      return
    }
    await loadDashboard(filters)
  }

  const loadAllTime = async () => {
    setFilters(EMPTY_FILTERS)
    await loadDashboard(EMPTY_FILTERS)
  }

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setError('')
  }

  const exportExcel = async () => {
    if (!dashboard || exporting || JSON.stringify(filters) !== JSON.stringify(appliedFilters)) return
    setExporting(true)
    const result = await ApiService.exportClickBusReportExcel(appliedFilters)
    setExporting(false)
    if (!result.success || !result.data) {
      notify.showError(result.error || 'Não foi possível exportar o relatório ClickBus.')
      return
    }
    const blobUrl = window.URL.createObjectURL(result.data)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = `relatorio-clickbus-${new Date().toISOString().slice(0, 10)}.xlsx`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(blobUrl)
    notify.showSuccess('Relatório ClickBus exportado com sucesso!')
  }

  const filtersChanged = JSON.stringify(filters) !== JSON.stringify(appliedFilters)

  return (
    <div className="clickbus-report">
      <section className="report-filter-panel clickbus-filter-panel">
        <div className="report-filter-heading">
          <div><span className="report-eyebrow">Filtros do relatório</span><h2>Período da análise</h2><p>Deixe as datas em branco para consultar todo o histórico ou informe um período de entrada.</p></div>
          {dashboard ? <div className="report-cache-pill"><i className="fas fa-clock" /><span>Consultado em<strong>{formatDateTime(dashboard.consultadoEm)}</strong></span></div> : null}
        </div>
        <form onSubmit={applyFilters}>
          <div className="clickbus-period-grid">
            <label><span>Data inicial <small>(opcional)</small></span><input type="date" value={filters.dataInicio} onChange={(event) => setFilters((current) => ({ ...current, dataInicio: event.target.value }))} /></label>
            <label><span>Data final <small>(opcional)</small></span><input type="date" value={filters.dataFim} onChange={(event) => setFilters((current) => ({ ...current, dataFim: event.target.value }))} /></label>
          </div>
          <div className="report-filter-actions">
            <button type="button" className="btn-secondary" onClick={clearFilters} disabled={!filters.dataInicio && !filters.dataFim}>Limpar datas</button>
            <button type="button" className="btn-secondary clickbus-all-time-button" onClick={loadAllTime} disabled={loading}><i className="fas fa-infinity" /> Consultar todo o período</button>
            <button type="button" className="btn-secondary report-export-button" onClick={exportExcel} disabled={!dashboard || filtersChanged || exporting} title={filtersChanged ? 'Aplique o novo período antes de exportar.' : 'Exportar dashboard e base completa em Excel'}><i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-file-excel'}`} /> {exporting ? 'Gerando Excel...' : 'Exportar relatório'}</button>
            <button type="submit" className="btn-primary" disabled={loading}><i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-filter'}`} /> {loading ? 'Calculando...' : 'Consultar'}</button>
          </div>
        </form>
      </section>

      {error ? <div className="report-error"><i className="fas fa-circle-exclamation" />{error}</div> : null}
      {!dashboard && !loading ? <div className="report-empty"><i className="fas fa-bus" /><h3>Dashboard ClickBus</h3><p>Clique em “Consultar todo o período” para analisar a vida completa da ClickBus ou use as datas opcionais acima.</p></div> : null}
      {loading && !dashboard ? <div className="report-empty"><i className="fas fa-spinner fa-spin" /><h3>Consultando a carteira ClickBus...</h3></div> : null}

      {dashboard ? <>
        <div className="report-stats-grid clickbus-stats-grid">
          <Stat label="Total de processos" value={formatNumber(dashboard.totalProcessos)} detail="No período de entrada" icon="fa-scale-balanced" />
          <Stat label="Valor da causa" value={formatCurrency(dashboard.valorCausa)} detail="Soma do período" icon="fa-sack-dollar" tone="blue" />
          <Stat label="Provisão ClickBus" value={formatCurrency(dashboard.valorProvisao)} detail="Provisionamento acumulado" icon="fa-chart-line" tone="purple" />
          <Stat label="Indenização ClickBus" value={formatCurrency(dashboard.indenizacaoClickBus)} detail="Total pago pela ClickBus" icon="fa-building-circle-check" tone="green" />
          <Stat label="Indenização viação" value={formatCurrency(dashboard.indenizacaoViacao)} detail="Total pago pelas viações" icon="fa-bus-simple" />
        </div>
        <div className="report-charts-grid">
          <section className="report-card report-chart wide"><div className="report-card-title"><div><span>Valores por ano e mês</span><h3>Evolução financeira mensal</h3></div></div><div className="report-chart-canvas"><ChartComponent type="bar" data={valueChart} /></div></section>
          <section className="report-card report-chart"><div className="report-card-title"><div><span>Volume mensal</span><h3>Processos por competência</h3></div></div><div className="report-chart-canvas"><ChartComponent type="bar" data={countChart} /></div></section>
        </div>
        <div className="report-rankings-grid clickbus-rankings"><Ranking title="Por causa raiz" items={dashboard.porCausaRaiz} /><Ranking title="Por responsabilidade" items={dashboard.porResponsabilidade} /></div>
      </> : null}
    </div>
  )
}
